import { _ } from "../../../../core/polyglot/polyglot.js";
import {
	checkIcon,
	circleIcon,
} from "../../../../core/elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import button from "@foretagsplatsen/finsit-design-system/widget-js/elements/button";

/**
 * Widget displaying the checklist for a period report.
 *
 * @param {function(string, object):void} [spec.toggleItemCallback] - Callback to check/uncheck an item in the checklist.
 * @param {function(string, string):void} [spec.insertCommentCallback] - Callback to insert a piece of text inside a section.
 * @param {function(string):string} [spec.sectionTitleRenderer] - Callback to get the title for a section from its id.
 * @param {function():boolean} [spec.isSequential] - Sequential settings for the checklist, returning true enables
 * only the first unchecked item, false enables every items.
 */
export default class PeriodReportChecklistWidget extends Widget {
	constructor({
		toggleItemCallback,
		insertCommentCallback,
		sectionTitleRenderer,
		isSequential = () => true,
	}) {
		super(...arguments);

		this._toggleItemCallback = toggleItemCallback;
		this._insertCommentCallback = insertCommentCallback;
		this._sectionTitleRenderer = sectionTitleRenderer;
		this._isSequential = isSequential;
	}

	setChecklist(checklist) {
		this._checklist = checklist;
		this.update();
	}

	renderContentOn(html) {
		if (!this._checklist) {
			return;
		}

		html.div(
			{
				klass: [
					"checklist",
					{ completed: this._checklist.isCompleted() },
				],
				"data-test": "period-report-checklist",
			},
			this._renderChecklistOn.bind(this),
			this._renderChecklistCompletionOn.bind(this),
		);
	}

	_renderChecklistOn(html) {
		html.div(
			{ klass: "groups" },
			this._checklist
				.getSections()
				.map((section) => this._sectionRenderer(section)),
		);
	}

	_renderChecklistCompletionOn(html) {
		if (!this._checklist.isCompleted()) {
			return;
		}

		html.div(
			{ klass: "completed-checklist-greeting" },
			html.i({ klass: "far fa-check-circle" }),
		);
	}

	_sectionRenderer(section) {
		const sectionItems = section.getItems();

		return (html) => {
			if (sectionItems.length <= 0) {
				return;
			}
			const firstUncheckedItem = this._checklist.firstUncheckedItem();

			html.div(
				{ klass: "group" },
				html.h4(this._sectionTitleRenderer(section.getId())),
				html.ul(
					sectionItems.map((item) => {
						const isFirstUncheckedItem =
							item === firstUncheckedItem;
						return this._itemRenderer(
							section,
							item,
							isFirstUncheckedItem,
						);
					}),
				),
			);
		};
	}

	_itemRenderer(section, checklistItem, isFirstUncheckedItem) {
		const isEnabled = !this._isSequential() || isFirstUncheckedItem;
		return (html) => {
			html.li(
				{
					klass: [
						"item",
						{ checked: checklistItem.isChecked() },
						{ disabled: !isEnabled },
					],
					"data-test": "checklist-item",
				},
				html.h5(
					this._itemIconRenderer(section, checklistItem),
					checklistItem.getLabel(),
				),
				(html) => {
					if (checklistItem.hasDescription()) {
						html.p(checklistItem.getDescription());
					}
					if (isEnabled && !checklistItem.isChecked()) {
						html.render(
							this._itemActionsRenderer(section, checklistItem),
						);
					}
				},
			);
		};
	}

	_itemIconRenderer(section, item) {
		return button(
			{
				as: "div",
				onClick: () => {
					this._toggleItemCallback(section.getId(), item);
				},
			},
			item.isChecked() ? checkIcon : circleIcon,
		);
	}

	_itemActionsRenderer(section, item) {
		if (!item.hasComment()) {
			return () => {};
		}

		return (html) => {
			html.div(
				{ klass: "actions" },
				this._itemActionRenderer(_("Insert comment"), () =>
					this._insertCommentCallback(
						section.getId(),
						item.getComment(),
					),
				),
			);
		};
	}

	_itemActionRenderer(label, callback) {
		return (html) => {
			html.a({ klass: "btn btn-xs btn-primary" }, label).click(callback);
		};
	}

	_toggleItem(item) {
		item.toggle();
		this.update();
	}
}
